import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import SelectDictTable from "components/SelectDictTable";
import { sortingKeys } from "constants/dictionary";
import { modalTypes } from "constants/types";
import {
  BUTTON_MULTIPLE,
  BUTTON_SINGLE,
  DESCRIPTION_MULTIPLE,
  DESCRIPTION_SINGLE,
  folderBy,
  HEADER_MULTIPLE,
  HEADER_SINGLE,
} from "layout/dictionarySearch/constants";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch } from "react-router-dom";
import { hideModal } from "slices/app/actions/modals";
import {
  setDictionaryToAllDocs,
  setNewDictionary,
} from "slices/dictionarySearch/actions/categories";

const ChangeDictionaryModal = ({ docId }) => {
  const dispatch = useDispatch();
  const match = useMatch("/workspace/:folderId/*");
  const { folderId } = match?.params || {};

  const dictionaries = useSelector(
    (state) => state.dictionarySearch.dictionaries
  );
  const by = useSelector((state) => state.dictionarySearch.config.folderBy);
  const activeDictionaryId = useSelector((state) =>
    by === folderBy.folder || (by === folderBy.document && docId === undefined)
      ? state.dictionarySearch.folder.dictionary.id
      : state.dictionarySearch.documents.find((d) => d.id === docId).dictionary
          .id
  );
  const filteredDictionaries = activeDictionaryId
    ? dictionaries.filter((dictionary) => dictionary.id !== activeDictionaryId)
    : dictionaries;

  const [selectedDictionaryId, setSelectedDictionaryId] = useState(null);
  const applyToMultiple = by === folderBy.document && docId === undefined;

  const headerConfig = [
    {
      title: `Dictionaries (${dictionaries.length - 1})`,
      sortBy: sortingKeys.name,
    },
    {
      title: "Created By",
      sortBy: sortingKeys.createdBy,
    },
    {
      title: "Created On",
      sortBy: sortingKeys.createdOn,
    },
    {
      title: "Last Updated",
      sortBy: sortingKeys.lastUpdated,
    },
    {
      title: "In Use",
      sortBy: sortingKeys.inUse,
    },
  ];

  const setDictionary = (dictionaryId) => {
    if (by === folderBy.folder) {
      dispatch(setNewDictionary({ dictionaryId, folderId }));
    } else if (applyToMultiple) {
      dispatch(setDictionaryToAllDocs({ dictionaryId, folderId }));
    } else {
      dispatch(setNewDictionary({ dictionaryId, folderId, documentId: docId }));
    }
  };

  const handleSelect = (dictionaryId) => {
    setSelectedDictionaryId(dictionaryId);
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.CHANGE_DICTIONARY));
  };

  const handleChangeClick = () => {
    setDictionary(selectedDictionaryId);
  };

  const modalBody = () => {
    return (
      <div className="change_dictionary__body">
        <span className="change_dictionary__body__description">
          {applyToMultiple ? DESCRIPTION_MULTIPLE : DESCRIPTION_SINGLE}
        </span>
        {applyToMultiple && (
          <span className="change_dictionary__body__note">
            NOTE: If you have a lot of docs & a larger dictionary, this might
            take a minute or two. Please be patient.
          </span>
        )}
        <SelectDictTable
          dictionaries={filteredDictionaries}
          selectedDictionaryId={selectedDictionaryId}
          headerConfig={headerConfig}
          initColumnWidths={[218, 86, 88, 98, 45]}
          minimalColumnWidths={[40, 40, 40, 40, 40]}
          onDictionarySelect={handleSelect}
          onEnter={setDictionary}
        />
      </div>
    );
  };

  return (
    <Modal
      data-test="changeDictionaryModal"
      className="fade-in change_dictionary"
      show
      onDismiss={handleCancel}
      backdrop
    >
      <Modal.Header>
        <b>{applyToMultiple ? HEADER_MULTIPLE : HEADER_SINGLE}</b>
      </Modal.Header>
      <Modal.Body>{modalBody()}</Modal.Body>
      <Modal.Footer>
        <NewButton
          onClick={handleCancel}
          variant={NewButtonVariant.l2}
          data-test="cancelButton"
          disabled={false}
        >
          Cancel
        </NewButton>

        <NewButton
          onClick={handleChangeClick}
          variant={NewButtonVariant.l1}
          data-testid="changeDictionaryButton"
          disabled={selectedDictionaryId === null}
        >
          {applyToMultiple ? BUTTON_MULTIPLE : BUTTON_SINGLE}
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

ChangeDictionaryModal.propTypes = {
  docId: PropTypes.number,
};

export default ChangeDictionaryModal;
